import {useState} from 'react';
import {IWasteCategory} from '../../../../models/wasteCategory';
import {IWasteType} from '../../../../models/wasteType';
import {ICreateCleanUpForm} from '../index';
import s from '../index.module.scss';

interface Props {
    form: ICreateCleanUpForm;
    wasteTypes: IWasteType[];
    wasteCategories: IWasteCategory[];
    onChange: (updates: Partial<ICreateCleanUpForm>) => void;
}

export default function Step2Litter({form, wasteTypes, wasteCategories, onChange}: Props) {
    const [expandedCategories, setExpandedCategories] = useState<string[]>([]);

    const updateWeight = (wasteTypeId: string, field: 'wet' | 'dry', value: string) => {
        onChange({
            collected_weights: {
                ...form.collected_weights,
                [wasteTypeId]: {
                    ...(form.collected_weights[wasteTypeId] ?? {wet: '', dry: ''}),
                    [field]: value,
                },
            },
        });
    };

    const toggleCategory = (categoryId: string) => {
        setExpandedCategories((prev) =>
            prev.includes(categoryId)
                ? prev.filter((id) => id !== categoryId)
                : [...prev, categoryId]
        );
    };

    const updateItem = (categoryId: string, itemId: string, value: string) => {
        onChange({
            collected_items: {
                ...form.collected_items,
                [categoryId]: {
                    ...(form.collected_items[categoryId] ?? {}),
                    [itemId]: value,
                },
            },
        });
    };

    const getFilledCount = (categoryId: string) =>
        Object.values(form.collected_items[categoryId] ?? {}).filter(
            (v) => v && parseInt(v) > 0
        ).length;

    return (
        <div className={s.stepBody}>
            <div className={s.formGroup}>
                <label className={s.checkboxLabel}>
                    <input
                        type="checkbox"
                        checked={form.no_litter_found}
                        onChange={(e) => onChange({no_litter_found: e.target.checked})}
                    />
                    No litter found
                </label>
            </div>

            {!form.no_litter_found && (
                <>
                    {wasteTypes.length > 0 && (
                        <>
                            <div className={s.subSectionTitle}>Collected Weight</div>
                            <div className={s.weightList}>
                                {wasteTypes.map((wt) => {
                                    const entry = form.collected_weights[wt.id] ?? {
                                        wet: '',
                                        dry: '',
                                    };
                                    return (
                                        <div key={wt.id} className={s.weightRow}>
                                            <span className={s.weightLabel}>{wt.title}</span>
                                            <div className={s.weightInputs}>
                                                <div className={s.weightField}>
                                                    <span className={s.weightUnit}>Dry</span>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="0.1"
                                                        placeholder="kg"
                                                        value={entry.dry}
                                                        onChange={(e) =>
                                                            updateWeight(wt.id, 'dry', e.target.value)
                                                        }
                                                        className={s.weightInput}
                                                    />
                                                </div>
                                                <div className={s.weightField}>
                                                    <span className={s.weightUnit}>Wet</span>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="0.1"
                                                        placeholder="kg"
                                                        value={entry.wet}
                                                        onChange={(e) =>
                                                            updateWeight(wt.id, 'wet', e.target.value)
                                                        }
                                                        className={s.weightInput}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}

                    {wasteCategories.length > 0 && (
                        <>
                            <div className={s.subSectionTitle}>Collected Items</div>
                            <div className={s.accordionList}>
                                {wasteCategories.map((cat) => {
                                    const isOpen = expandedCategories.includes(cat.id);
                                    const filled = getFilledCount(cat.id);
                                    return (
                                        <div key={cat.id} className={s.accordionSection}>
                                            <button
                                                type="button"
                                                className={`${s.accordionHeader} ${isOpen ? s.accordionOpen : ''}`}
                                                onClick={() => toggleCategory(cat.id)}
                                            >
                                                <span className={s.accordionChevron}>
                                                    {isOpen ? '▾' : '▸'}
                                                </span>
                                                <span className={s.accordionTitle}>
                                                    {cat.title}
                                                </span>
                                                {filled > 0 && (
                                                    <span className={s.filledBadge}>
                                                        {filled} filled
                                                    </span>
                                                )}
                                            </button>
                                            {isOpen && (
                                                <div className={s.accordionBody}>
                                                    {cat.items.map((item) => {
                                                        const amount =
                                                            form.collected_items[cat.id]?.[
                                                                item.id
                                                            ] ?? '';
                                                        return (
                                                            <div
                                                                key={item.id}
                                                                className={s.itemRow}
                                                            >
                                                                <span className={s.itemName}>
                                                                    {item.name}
                                                                </span>
                                                                <input
                                                                    type="number"
                                                                    min="0"
                                                                    placeholder="0"
                                                                    value={amount}
                                                                    onChange={(e) =>
                                                                        updateItem(
                                                                            cat.id,
                                                                            item.id,
                                                                            e.target.value
                                                                        )
                                                                    }
                                                                    className={s.itemInput}
                                                                />
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}

                    {wasteTypes.length === 0 && wasteCategories.length === 0 && (
                        <p className={s.emptyState}>
                            No waste types configured for this program.
                        </p>
                    )}
                </>
            )}
        </div>
    );
}
